import React, { useEffect, useState, useRef } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Input, Select, Textarea, Field, Modal, Spinner } from '../ui.jsx';
import { ContactLink } from '../widgets/ContactQuickEdit.jsx';
import { useTerminology } from '../hooks/useTerminology.js';

const JOB_STATUSES = ['Scheduled','Booked','Dispatched','En Route','On Site','Completed','Needs Attention','Cancelled'];
const STAT_COLOR = { Scheduled:G.blue, Booked:G.cyan, Dispatched:G.purple, 'En Route':G.cyan, 'On Site':G.orange, Completed:G.green, 'Needs Attention':G.yellow, Cancelled:G.red };

export function Jobs({ toast }) {
  const { cap, plural } = useTerminology();
  const [jobs, setJobs] = useState([]);
  const [techs, setTechs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [routing, setRouting] = useState(null); // { job, suggestions }

  const load = async () => {
    try {
      const [j,t,c] = await Promise.all([api('/api/jobs'), api('/api/techs'), api('/api/contacts').catch(()=>[])]);
      setJobs(j); setTechs(t); setContacts(c);
    } catch(e) { toast(e.message,'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const advance = async job => {
    const next = { Scheduled:'Dispatched', Booked:'Dispatched', Dispatched:'En Route', 'En Route':'On Site', 'On Site':'Completed' }[job.status];
    if (!next) return;
    try { await api(`/api/jobs/${job.id}`, { method:'PATCH', body:{ status: next } }); load(); toast(`Job → ${next}`); }
    catch(e) { toast(e.message,'error'); }
  };

  const smartRoute = async job => {
    try {
      const result = await api('/api/integrations/routing/suggest', { method:'POST', body:{ jobId: job.id, date: job.scheduledDate, durationMinutes: job.durationMinutes } });
      setRouting({ job, ...result });
    } catch(e) { toast(e.message,'error'); }
  };

  const assign = async (job, techId) => {
    try {
      await api('/api/integrations/routing/assign', { method:'POST', body:{ jobId: job.id, techId } });
      setRouting(null); load(); toast('Tech assigned & dispatched');
    } catch(e) { toast(e.message,'error'); }
  };

  const cols = ['Scheduled','Booked','Dispatched','En Route','On Site','Completed'];
  const byCol = Object.fromEntries(cols.map(c => [c, jobs.filter(j => j.status === c)]));

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>{cap('visit')} board <span style={{ color:G.textMuted, fontWeight:500, fontSize:18 }}>({jobs.length})</span></h1>
      <div style={{ display:'flex', gap:8 }}>
        <Btn v="ghost" onClick={()=>window.location.href='/api/jobs/export.csv'}>↓ Export CSV</Btn>
        <Btn v="primary" onClick={()=>setEditing({ status:'Scheduled', durationMinutes:120 })}>+ New {cap('visit')}</Btn>
      </div>
    </div>

    {loading ? <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner sz={32}/></div>
    : <div style={{ display:'grid', gridTemplateColumns:`repeat(${cols.length}, 1fr)`, gap:12, overflowX:'auto' }}>
      {cols.map(col => (
        <div key={col} style={{ minWidth:240 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8, padding:'0 4px' }}>
            <div style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', color:STAT_COLOR[col] }}>{col}</div>
            <span style={{ fontSize:12, color:G.textMuted, background:G.input, padding:'2px 8px', borderRadius:10 }}>{byCol[col].length}</span>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {byCol[col].map(j => (
              <div key={j.id} onClick={()=>setEditing(j)} style={{ ...styles.card, padding:14, cursor:'pointer', borderColor: STAT_COLOR[col]+'44' }}>
                <div style={{ fontWeight:600, fontSize:14 }}>{j.name}</div>
                <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}><ContactLink id={j.contactId}>{j.contact}</ContactLink></div>
                <div style={{ fontSize:12, color:G.textMuted }}>{j.address || '—'}</div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:8 }}>
                  <Badge c={G.purple} bg={G.purpleDim}>{j.tech || 'Unassigned'}</Badge>
                  <span style={{ fontSize:13, fontWeight:600, color:G.green }}>{fmtUsd(j.est||0)}</span>
                </div>
                <div style={{ display:'flex', gap:6, marginTop:10 }} onClick={e=>e.stopPropagation()}>
                  {!j.tech && <Btn sz="xs" v="cyan" onClick={()=>smartRoute(j)}>🎯 Auto-Assign</Btn>}
                  {col !== 'Completed' && <Btn sz="xs" v="primary" onClick={()=>advance(j)}>Advance →</Btn>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>}

    <JobModal job={editing} techs={techs} contacts={contacts} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Job saved'); }} />
    <RoutingModal data={routing} onClose={()=>setRouting(null)} onAssign={assign} techs={techs} />
  </div>;
}

function JobModal({ job, techs, contacts, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(job||{}); }, [job]);
  if (!job) return null;
  const set = k => e => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const isNew = !form.id;

  const save = async () => {
    try {
      if (isNew) await api('/api/jobs', { method:'POST', body: form });
      else await api(`/api/jobs/${form.id}`, { method:'PATCH', body: form });
      onSaved();
    } catch(e) { alert(e.message); }
  };

  const isProject = form.jobType === 'project';
  const phases = Array.isArray(form.phases) ? form.phases : [];
  const updatePhase = (i, patch) => {
    const np = [...phases];
    np[i] = { ...np[i], ...patch };
    setForm({ ...form, phases: np });
  };
  const addPhase = () => setForm({ ...form, phases: [...phases, { name: `Phase ${phases.length + 1}`, status:'pending', scheduled_date:'', amount: 0 }] });
  const removePhase = (i) => setForm({ ...form, phases: phases.filter((_, j) => j !== i) });

  return <Modal open onClose={onClose} width={760}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <h2 style={{ margin:0 }}>{isNew?'New Job':form.name}</h2>
      {/* Service vs Project toggle — project mode unlocks phases + contract amount */}
      <div style={{ display:'flex', gap:4, background:G.input, padding:3, borderRadius:8 }}>
        {[
          { v:'service', label:'🔧 Service Call', sub:'Single visit' },
          { v:'project', label:'🏗 Project', sub:'Multi-phase' },
        ].map(opt => (
          <button key={opt.v} onClick={()=>setForm({ ...form, jobType: opt.v })} title={opt.sub} style={{
            padding:'6px 12px', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer',
            border:'none', background: (form.jobType||'service') === opt.v ? G.purple : 'transparent',
            color: (form.jobType||'service') === opt.v ? '#fff' : G.text,
          }}>{opt.label}</button>
        ))}
      </div>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Job Name *"><Input value={form.name||''} onChange={set('name')} placeholder={isProject ? 'Whole-house rewire' : 'AC Replacement'}/></Field>
      <Field label="Contact">
        <Select value={form.contactId||''} onChange={e => {
          const c = contacts.find(x => x.id === e.target.value);
          setForm({ ...form, contactId: e.target.value, contact: c ? `${c.firstName} ${c.lastName||''}`.trim() : form.contact, address: c ? c.address : form.address });
        }}>
          <option value="">— select —</option>
          {contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
        </Select>
      </Field>
      <Field label="Address"><Input value={form.address||''} onChange={set('address')} /></Field>
      <Field label="Estimated Value"><Input type="number" value={form.est||0} onChange={set('est')} /></Field>
      <Field label="Tech">
        <Select value={form.techId||''} onChange={e => {
          const t = techs.find(x => x.id === e.target.value);
          setForm({ ...form, techId: e.target.value, tech: t?.name || '' });
        }}>
          <option value="">Unassigned</option>
          {techs.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
      </Field>
      <Field label="Duration (minutes)"><Input type="number" value={form.durationMinutes||120} onChange={set('durationMinutes')} /></Field>
      <Field label="Status">
        <Select value={form.status||'Scheduled'} onChange={set('status')}>
          {JOB_STATUSES.map(s => <option key={s}>{s}</option>)}
        </Select>
      </Field>
      <Field label="Scheduled Date"><Input type="date" value={form.scheduledDate||''} onChange={set('scheduledDate')} /></Field>
      <Field label="Start Time"><Input type="time" value={form.startTime||''} onChange={set('startTime')} /></Field>
      <Field label="Notes"><Input value={form.notes||''} onChange={set('notes')} /></Field>
      {isProject && <>
        <Field label="End Date (projected)"><Input type="date" value={form.endDate||''} onChange={set('endDate')} /></Field>
        <Field label="Total Contract Amount">
          <Input type="number" step="0.01" value={form.totalContractAmount||0} onChange={set('totalContractAmount')} placeholder="50000"/>
        </Field>
      </>}
    </div>

    {isProject && <div style={{ ...styles.card, padding:14, marginTop:14, borderColor:G.purple+'44' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
        <strong>🏗 Phases</strong>
        <span style={{ fontSize:12, color:G.textMuted }}>
          Billed: ${(Number(form.amountBilledToDate||0)).toLocaleString()} / ${(Number(form.totalContractAmount||0)).toLocaleString()}
        </span>
      </div>
      {phases.length === 0 && <div style={{ padding:14, color:G.textMuted, fontSize:13, textAlign:'center', background:G.input, borderRadius:8 }}>
        No phases yet — break your project into milestones (demo, rough-in, finish, etc.) to track progress + bill per phase.
      </div>}
      {phases.map((p, i) => (
        <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 120px 100px 100px 28px', gap:8, marginBottom:8, padding:'8px 10px', background:G.input, borderRadius:6 }}>
          <Input value={p.name||''} onChange={e=>updatePhase(i, { name: e.target.value })} placeholder="Phase name"/>
          <Input type="date" value={p.scheduled_date||''} onChange={e=>updatePhase(i, { scheduled_date: e.target.value })}/>
          <Input type="number" value={p.amount||0} onChange={e=>updatePhase(i, { amount: Number(e.target.value)||0 })} placeholder="$"/>
          <select value={p.status||'pending'} onChange={e=>updatePhase(i, { status: e.target.value })} style={{ ...styles.inp, padding:'6px 8px', fontSize:12 }}>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="billed">Billed</option>
            <option value="paid">Paid</option>
          </select>
          <Btn sz="xs" v="ghost" onClick={()=>removePhase(i)}>✕</Btn>
        </div>
      ))}
      <Btn sz="xs" v="ghost" onClick={addPhase}>+ Add Phase</Btn>
    </div>}

    {/* Mandatory Service Call Forms — shows the same form the tech sees.
        Blocks close on the server so it works from both surfaces. */}
    {!isNew && form.id && <JobCallForms jobId={form.id}/>}

    {/* Materials/van checklist — shows up only for saved jobs (need an id to query) */}
    {!isNew && form.id && <MaterialsChecklist jobId={form.id}/>}

    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}

// Desktop mirror of the TechApp's ServiceCallFormsPanel. Same endpoint, same
// values, same completion gate. Shows in the JobModal so owners + dispatchers
// can also see/fill the mandatory service-call form.
function JobCallForms({ jobId }) {
  const [forms, setForms] = useState(null);
  const [values, setValues] = useState({});
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState({});

  const load = () => api(`/api/call-forms/for-job/${jobId}`).then(r => {
    setForms(r.forms || []);
    const initV = {};
    (r.forms || []).forEach(f => { initV[f.form.id] = f.response.values || {}; });
    setValues(initV);
    if ((r.forms || []).length > 0) {
      const incomplete = r.forms.find(f => !f.response.isComplete);
      if (incomplete) setExpanded(e => ({ ...e, [incomplete.form.id]: true }));
    }
  }).catch(() => setForms([]));
  useEffect(() => { load(); }, [jobId]);

  const saveForm = async (formId) => {
    setSaving(s => ({ ...s, [formId]: true }));
    try { await api(`/api/call-forms/response/${formId}/${jobId}`, { method:'PATCH', body:{ values: values[formId] || {} }}); load(); }
    catch (e) { alert(e.message); }
    finally { setSaving(s => ({ ...s, [formId]: false })); }
  };
  const setVal = (formId, key, v) => setValues(prev => ({ ...prev, [formId]: { ...(prev[formId] || {}), [key]: v }}));
  const hasVal = (v) => v != null && (typeof v !== 'string' || v.trim().length > 0);

  if (!forms) return null;
  if (forms.length === 0) return <div style={{ marginTop:14, padding:14, border:`2px dashed ${G.orange}88`, background:G.orange+'11', borderRadius:8 }}>
    <div style={{ fontSize:11, color:G.orange, fontWeight:800, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:4 }}>⚠ No mandatory forms attached</div>
    <div style={{ fontSize:13, lineHeight:1.5 }}>This account has no service-call forms yet. Agency admin: attach one in Agency → this account → ⚡ Vertical Setup (or 🚫 Call Forms).</div>
  </div>;

  return <div style={{ marginTop:14 }}>
    {forms.map(({ form, response, progress }) => {
      const complete = response.isComplete;
      const missingN = progress.missingCount;
      const v = values[form.id] || {};
      const isExp = !!expanded[form.id];
      return <div key={form.id} style={{ ...styles.card, padding:0, marginBottom:10, overflow:'hidden', border:`2px solid ${complete?G.green:G.red}` }}>
        <div onClick={()=>setExpanded(e => ({ ...e, [form.id]: !e[form.id] }))}
          style={{ padding:'12px 14px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', background: complete ? G.green+'11' : G.red+'11' }}>
          <div>
            <div style={{ fontSize:11, color:complete?G.green:G.red, fontWeight:800, textTransform:'uppercase' }}>
              {complete ? '✓ COMPLETE' : `⚠ MANDATORY · ${missingN} FIELD${missingN===1?'':'S'} MISSING`}
            </div>
            <div style={{ fontSize:14, fontWeight:700, marginTop:2 }}>{form.name}</div>
          </div>
          <span style={{ fontSize:18, color:G.textMuted }}>{isExp ? '▲' : '▼'}</span>
        </div>
        {isExp && <div style={{ padding:14 }}>
          {form.description && <div style={{ fontSize:12, color:G.textMuted, marginBottom:12, lineHeight:1.5, padding:'8px 12px', background:G.input, borderRadius:6 }}>{form.description}</div>}
          {(form.schema.sections || []).map(section => (
            <div key={section.id} style={{ marginBottom:16, paddingBottom:12, borderBottom:`1px solid ${G.border}` }}>
              <div style={{ fontSize:13, fontWeight:800, marginBottom:8, textTransform:'uppercase' }}>{section.title}</div>
              {section.type === 'instructions' ? (
                <ol style={{ paddingLeft:20, margin:0 }}>{section.steps.map((s, i) => <li key={i} style={{ fontSize:13, lineHeight:1.6, marginBottom:4 }}>{s}</li>)}</ol>
              ) : section.type === 'tier_table' ? (
                <JobTierTable section={section} formId={form.id} values={v} setVal={setVal} hasVal={hasVal}/>
              ) : section.type === 'checklist_before_after' ? (
                <JobChecklistBeforeAfter section={section} formId={form.id} values={v} setVal={setVal} onSave={()=>saveForm(form.id)}/>
              ) : (
                (section.fields || []).map(f => {
                  const key = `${section.id}.${f.id}`;
                  const missing = f.required && !hasVal(v[key]);
                  return <div key={f.id} style={{ marginBottom:12 }}>
                    <label style={{ display:'block', fontSize:12, fontWeight:600, color: missing ? G.red : G.text, marginBottom:4 }}>{f.label}{f.required && ' *'}</label>
                    {f.type === 'text' && <Input value={v[key]||''} onChange={e=>setVal(form.id, key, e.target.value)} onBlur={()=>saveForm(form.id)} placeholder={f.placeholder||''}/>}
                    {f.type === 'textarea' && <Textarea value={v[key]||''} onChange={e=>setVal(form.id, key, e.target.value)} onBlur={()=>saveForm(form.id)} style={{ minHeight:60 }}/>}
                    {f.type === 'number' && <Input type="number" value={v[key]||''} onChange={e=>setVal(form.id, key, e.target.value)} onBlur={()=>saveForm(form.id)}/>}
                    {f.type === 'yesno' && <div style={{ display:'flex', gap:8 }}>{['Yes','No'].map(opt => <button key={opt} onClick={()=>{ setVal(form.id, key, opt); setTimeout(()=>saveForm(form.id), 100); }} style={{ flex:1, padding:'10px 14px', borderRadius:8, border:`1px solid ${v[key]===opt?G.green:G.border}`, background:v[key]===opt?G.green+'22':G.card, color:v[key]===opt?G.green:G.text, fontWeight:700, cursor:'pointer' }}>{opt}</button>)}</div>}
                    {f.type === 'select' && <Select value={v[key]||''} onChange={e=>{ setVal(form.id, key, e.target.value); setTimeout(()=>saveForm(form.id), 100); }}><option value="">Pick one…</option>{f.options.map(o => <option key={o} value={o}>{o}</option>)}</Select>}
                  </div>;
                })
              )}
            </div>
          ))}
          <Btn v="primary" onClick={()=>saveForm(form.id)} disabled={saving[form.id]}>{saving[form.id] ? 'Saving…' : 'Save'}</Btn>
        </div>}
      </div>;
    })}
  </div>;
}

function JobTierTable({ section, formId, values, setVal, hasVal }) {
  const tiers = section.tiers || [];
  const rowCount = section.rowCount || 10;
  return <div style={{ overflow:'auto' }}>
    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:520 }}>
      <thead><tr>
        <th style={{ textAlign:'left', padding:'6px 8px', color:G.textMuted, fontSize:11 }}>Line Item</th>
        {tiers.map((t, i) => <th key={i} style={{ textAlign:'center', padding:'6px 8px', color:G.textMuted, fontSize:11 }}>{t}</th>)}
      </tr></thead>
      <tbody>
        {Array.from({ length: rowCount }, (_, ri) => (
          <tr key={ri} style={{ borderTop:`1px solid ${G.border}` }}>
            <td style={{ padding:'4px 8px' }}>
              <Input value={values[`${section.id}.line.${ri}.desc`]||''} onChange={e=>setVal(formId, `${section.id}.line.${ri}.desc`, e.target.value)} placeholder={`Item ${ri+1}`} style={{ fontSize:12, padding:'6px 8px' }}/>
            </td>
            {tiers.map((_, ti) => (
              <td key={ti} style={{ padding:'4px 4px' }}>
                <Input value={values[`${section.id}.line.${ri}.${ti}`]||''} onChange={e=>setVal(formId, `${section.id}.line.${ri}.${ti}`, e.target.value)} placeholder="$" style={{ fontSize:12, padding:'6px 8px', textAlign:'right' }}/>
              </td>
            ))}
          </tr>
        ))}
        {(section.addons || []).map(a => (
          <tr key={a.id} style={{ borderTop:`1px solid ${G.border}`, background:G.input+'80' }}>
            <td style={{ padding:'4px 8px', fontWeight:700 }}>{a.label}</td>
            {tiers.map((_, ti) => (
              <td key={ti} style={{ padding:'4px 4px' }}>
                <Input value={values[`${section.id}.addon.${a.id}.${ti}`]||''} onChange={e=>setVal(formId, `${section.id}.addon.${a.id}.${ti}`, e.target.value)} placeholder="$" style={{ fontSize:12, padding:'6px 8px', textAlign:'right' }}/>
              </td>
            ))}
          </tr>
        ))}
        {(section.totals || []).map((t, i) => (
          <tr key={t.id} style={{ borderTop:i===0?`2px solid ${G.border}`:`1px solid ${G.border}`, background:t.id==='total'?G.green+'11':'transparent' }}>
            <td style={{ padding:'6px 8px', fontWeight:800, textTransform:'uppercase' }}>{t.label}{t.required && ' *'}</td>
            {tiers.map((_, ti) => {
              const key = `${section.id}.total.${t.id}.${ti}`;
              const missing = t.required && !hasVal(values[key]);
              return <td key={ti} style={{ padding:'4px 4px' }}>
                <Input value={values[key]||''} onChange={e=>setVal(formId, key, e.target.value)}
                  placeholder="$"
                  style={{ fontSize:12, padding:'6px 8px', textAlign:'right', fontWeight:700, border:`1px solid ${missing?G.red:G.border}`, color: t.id==='total'?G.green:G.text }}/>
              </td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>;
}

// Desktop Jobs modal version of the before/after checklist. Same shape as
// the tech one but without capture=environment on the file input.
function JobChecklistBeforeAfter({ section, formId, values, setVal, onSave }) {
  const halves = [
    section.before ? { key: 'before', label: 'BEFORE — Arrival', cfg: section.before, tint: G.orange } : null,
    section.after  ? { key: 'after',  label: 'AFTER — Departure', cfg: section.after,  tint: G.green  } : null,
  ].filter(Boolean);
  return <div style={{ display:'grid', gap:12 }}>
    {halves.map(h => (
      <div key={h.key} style={{ border:`1px solid ${h.tint}44`, background:h.tint+'0a', borderRadius:10, padding:12 }}>
        <div style={{ fontSize:12, fontWeight:800, color:h.tint, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:10 }}>{h.label}</div>
        {(h.cfg.checklist || []).map(item => {
          const key = `${section.id}.${h.key}.${item.id}`;
          const checked = values[key] === true;
          return <div key={item.id} onClick={()=>{ setVal(formId, key, !checked); setTimeout(onSave, 100); }}
            style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 4px', cursor:'pointer' }}>
            <div style={{ width:22, height:22, borderRadius:6, border:`2px solid ${checked?h.tint:G.border}`,
              background: checked ? h.tint : 'transparent', flexShrink:0,
              display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:800, fontSize:13 }}>
              {checked ? '✓' : ''}
            </div>
            <div style={{ fontSize:13, textDecoration: checked ? 'line-through' : 'none', color: checked ? G.textMuted : G.text }}>
              {item.label}{item.required && !checked && <span style={{ color:G.red }}> *</span>}
            </div>
          </div>;
        })}
        <JobPhotoField formId={formId} valueKey={`${section.id}.${h.key}.photo`}
          currentUrl={values[`${section.id}.${h.key}.photo`]}
          label={h.cfg.photoLabel || `${h.key === 'before' ? 'Before' : 'After'} Photo`}
          required={h.cfg.photoRequired} tint={h.tint} setVal={setVal} onSaved={onSave}/>
      </div>
    ))}
  </div>;
}

function JobPhotoField({ formId, valueKey, currentUrl, label, required, tint, setVal, onSaved }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const base64 = await new Promise((res, rej) => {
        const rd = new FileReader();
        rd.onload = () => res(String(rd.result || ''));
        rd.onerror = () => rej(new Error('read failed'));
        rd.readAsDataURL(file);
      });
      const r = await fetch('/api/uploads', {
        method:'POST', credentials:'include',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ name: file.name, mime: file.type, base64 }),
      });
      if (!r.ok) throw new Error(`upload failed (${r.status})`);
      const j = await r.json();
      if (j.url) { setVal(formId, valueKey, j.url); setTimeout(onSaved, 50); }
    } catch {} finally { setBusy(false); }
  };
  const missing = required && !currentUrl;
  return <div style={{ marginTop:10, padding:10, borderRadius:8, border:`1px dashed ${missing ? G.red : tint}66` }}>
    <div style={{ fontSize:12, fontWeight:700, color: missing ? G.red : tint, marginBottom:8 }}>📸 {label}{required && !currentUrl && ' · required'}</div>
    <input ref={inputRef} type="file" accept="image/*" style={{ display:'none' }} onChange={e=>upload(e.target.files?.[0])}/>
    {currentUrl ? (
      <div style={{ display:'flex', gap:10 }}>
        <a href={currentUrl} target="_blank" rel="noreferrer"><img src={currentUrl} alt="" style={{ width:80, height:80, objectFit:'cover', borderRadius:6, border:`1px solid ${G.border}` }}/></a>
        <button onClick={()=>inputRef.current?.click()} disabled={busy}
          style={{ padding:'8px 12px', borderRadius:6, background:G.input, color:G.text, border:`1px solid ${G.border}`, cursor:'pointer', fontSize:12 }}>{busy?'…':'Replace'}</button>
      </div>
    ) : (
      <button onClick={()=>inputRef.current?.click()} disabled={busy}
        style={{ padding:'10px 14px', borderRadius:6, background:tint, color:'#fff', border:'none', fontWeight:700, cursor:'pointer', fontSize:13 }}>{busy?'Uploading…':'Upload Photo'}</button>
    )}
  </div>;
}

function MaterialsChecklist({ jobId }) {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    let alive = true;
    api(`/api/jobs/${jobId}/materials-checklist`).then(d => { if (alive) setData(d); }).catch(()=>{});
    return () => { alive = false; };
  }, [jobId]);
  if (!data) return null;
  return <div style={{ ...styles.card, padding:14, marginTop:14, borderColor: G.cyan+'66' }}>
    <div onClick={()=>setCollapsed(!collapsed)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ fontSize:18 }}>🚐</span>
        <strong style={{ fontSize:14 }}>Van / Truck Checklist</strong>
        <span style={{ fontSize:11, color:G.textMuted }}>
          {data.vertical ? `${data.vertical} defaults` : 'Generic'}
          {data.estimateStatus ? ` · Estimate ${data.estimateStatus}` : ' · No estimate'}
          {' · '}{data.totalItems} items
        </span>
      </div>
      <span style={{ fontSize:12, color:G.textMuted }}>{collapsed ? '▸ expand' : '▾ collapse'}</span>
    </div>
    {!collapsed && <div style={{ marginTop:10 }}>
      {data.sections.map(section => (
        <div key={section.key} style={{ marginBottom:12 }}>
          <div style={{ fontSize:12, fontWeight:700, color:G.cyan, marginBottom:4, textTransform:'uppercase', letterSpacing:'.04em' }}>{section.title}</div>
          {(!section.items || section.items.length === 0)
            ? <div style={{ fontSize:12, color:G.textMuted, padding:'4px 0', fontStyle:'italic' }}>{section.empty || 'None'}</div>
            : <ul style={{ margin:0, paddingLeft:18, fontSize:13, lineHeight:1.7 }}>
                {section.items.map((it, i) => (
                  <li key={i} style={{ listStyleType: it.notes_only ? 'square' : 'circle', color: it.notes_only ? G.textMuted : G.text }}>{it.label}</li>
                ))}
              </ul>
          }
        </div>
      ))}
    </div>}
  </div>;
}

function RoutingModal({ data, onClose, onAssign }) {
  if (!data) return null;
  return <Modal open onClose={onClose} width={580}>
    <h2 style={{ margin:'0 0 8px 0' }}>🎯 Smart Routing</h2>
    <div style={{ color:G.textMuted, marginBottom:18, fontSize:14 }}>Suggested techs for: <strong style={{ color:G.text }}>{data.job.name}</strong> on {data.targetDate}. Score factors current weekly load + same-day load + travel buffer.</div>
    {data.suggestions.length === 0 && <div style={{ padding:20, color:G.textMuted, textAlign:'center' }}>No active techs available — add team members first.</div>}
    {data.suggestions.map((s, i) => (
      <div key={s.id} style={{ ...styles.card, marginBottom:8, display:'flex', alignItems:'center', justifyContent:'space-between', borderColor: i===0?G.green+'66':G.cardBorder }}>
        <div>
          <div style={{ fontWeight:600 }}>{s.name} {i===0 && <Badge c={G.green} bg={G.greenLight}>Best Match</Badge>}</div>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{s.role} · Week load: {s.weekLoad} jobs · Today: {s.dayLoad} · Score: {s.score}</div>
        </div>
        <Btn v="primary" sz="sm" onClick={()=>onAssign(data.job, s.id)}>Assign</Btn>
      </div>
    ))}
    <div style={{ display:'flex', justifyContent:'flex-end', marginTop:10 }}><Btn onClick={onClose}>Close</Btn></div>
  </Modal>;
}
